'use client';

import type { FC } from 'react';

import cn from 'classnames';
import { useSetAtom } from 'jotai';

import Button from '@gds/Button/Button';
import { openQuickFilterAtom } from '@plp/Atoms/QuickFilterOpenAtom';
import { MultiSelect } from '@plp/Components/MultiSelect/MultiSelect';

import type { FilterControlBaseProps } from '../../FilterPanel.entity';
import ApplyButton from '../ApplyButton/ApplyButton';
import SearchInput from '../SearchInput/SearchInput';
import useModelFilter from './UseModelFilter';

import styles from './ModelFilter.module.css';

type MakeFilterProps = FilterControlBaseProps;

const ModelFilter: FC<MakeFilterProps> = ({
  strings,
  triggerSearch,
  searchIsActive,
  locale,
  isPlpFiltersSeoEnabled,
  tenant,
  applyButton,
}) => {
  const {
    isSearching,
    handleSelect,
    handleSearchChange,
    searchValue,
    resetFilter,
    filteredOptions: filteredModels,
    allOptions: allModels,
    selectedOptions: selectedModels,
    handleApply,
  } = useModelFilter(locale, isPlpFiltersSeoEnabled, tenant, applyButton);

  const setOpenQuickFilter = useSetAtom(openQuickFilterAtom);

  const modelContainerClass = cn(
    styles.models,
    {
      [styles.searching]: searchIsActive,
    },
    { [styles.applyButtonModels]: applyButton },
  );

  const handleModelApply = () => {
    handleApply();
    setOpenQuickFilter(null);
  };

  return (
    <div>
      <SearchInput
        searchValue={searchValue}
        isSearching={isSearching}
        handleSearchChange={handleSearchChange}
        placeholder={strings.searchPlaceholder}
        triggerSearch={triggerSearch!}
        searchIsActive={searchIsActive!}
        parentId="model"
      />
      <Button
        variant="link"
        onClick={resetFilter}
        disabled={selectedModels.size === 0}
        size="small"
        className={styles.resetButton}
        dataTestId="reset-model-filter"
      >
        {strings.reset}
      </Button>
      <div className={modelContainerClass}>
        <MultiSelect
          groupName="model"
          items={searchValue ? filteredModels : allModels}
          onSelect={handleSelect}
          dataTestId="model-menu"
          noResultText={strings.noResults}
          groupBy="make"
          isGrouped
          keyAsTitle
          shouldOrder
          applyButton={applyButton}
          hideScrollbar={applyButton}
        />
      </div>
      {applyButton && <ApplyButton handleApply={handleModelApply} ctaText={strings.apply} />}
    </div>
  );
};

export default ModelFilter;
